/**
 * 1.6.0 the agent API gateway: the workflow editor's part of Call site service (node call_site_service) and of the
 * repeat_until check "Ask a site service" (check.type 'site_service'). Stream W's file (Builder.tsx, GraphCanvas.tsx
 * and NodeInspector.tsx only call in; docs/dev/mcp-1.6.0-build.md):
 *
 *  - SiteServiceEditor / SiteServiceCheckFields: an agent picker (agents whose gateway is on, from
 *    useSiteServices), then the service it reports, the method (only those the service allows), the path with a hint
 *    of the allowed patterns, query and payload rows (templated), and a preview of the URL (origin + path);
 *  - describeSiteServiceStep: the canvas line ("newsroom · GET /stories/{{…}}");
 *  - siteServiceStepDefaults: a new step's or check's fields.
 */
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Network, Plus, Trash2 } from 'lucide-react';
import { matchServiceRequest, type AgentSiteServicesDTO, type AutomationNode, type GatewayMethod, type RepeatCheck, type SiteServiceDTO } from '@prism/contracts';
import { useSiteServices } from '../../api/hooks/site-services';

type SiteServiceNode = Extract<AutomationNode, { type: 'call_site_service' }>;
type SiteServiceCheck = Extract<RepeatCheck, { type: 'site_service' }>;
type SiteCall = Pick<SiteServiceNode, 'agentId' | 'service' | 'method' | 'path' | 'query' | 'payload'>;

export const siteServiceIcon = <Network size={14} />;

const NO_AGENT = '00000000-0000-4000-8000-000000000000';

/** A new Call site service step's fields (also a new site_service check's, with type). */
export function siteServiceStepDefaults(): Omit<SiteServiceNode, 'id' | 'type' | 'position'> {
  return { agentId: NO_AGENT, service: 'service', method: 'GET', path: '/', query: {}, payload: {} };
}

export function describeSiteServiceStep(n: SiteServiceNode | SiteServiceCheck): string {
  if (n.agentId === NO_AGENT) return 'Choose a storage agent';
  return `${n.service} · ${n.method} ${n.path}`;
}

export function SiteServiceEditor({ node, set }: { node: SiteServiceNode; set: (patch: Partial<AutomationNode>) => void }) {
  return (
    <SiteCallFields idPrefix="n-ss" call={node} onChange={(patch) => set(patch as Partial<AutomationNode>)}
      answer={<>The answer is <span className="mono">{`{{nodes.${node.id}.output.response…}}`}</span> (JSON, or the text) and the status
        {' '}<span className="mono">{`{{nodes.${node.id}.output.status}}`}</span> for later steps. 2xx continues; 429 and 5xx are retried; other 4xx fail the step.
        An agent that is not connected is waited for.</>} />
  );
}

export function SiteServiceCheckFields({ check, onChange }: { check: SiteServiceCheck; onChange: (check: SiteServiceCheck) => void }) {
  return (
    <SiteCallFields idPrefix="n-ssc" call={check} onChange={(patch) => onChange({ ...check, ...patch })}
      answer={<>Each check’s answer is <span className="mono">response</span> and <span className="mono">status</span> of this step’s output for the conditions
        (e.g. <span className="mono">response.state</span> equals <span className="mono">ready</span>). Any status is an answer; a timeout counts as “not yet”.</>} />
  );
}

function SiteCallFields({ idPrefix, call, onChange, answer }: { idPrefix: string; call: SiteCall; onChange: (patch: Partial<SiteCall>) => void; answer: ReactNode }) {
  const q = useSiteServices();
  const agents = q.data?.items ?? [];
  const usable = agents.filter((a) => a.state === 'on');
  const agent: AgentSiteServicesDTO | undefined = agents.find((a) => a.agentId === call.agentId);
  const service: SiteServiceDTO | undefined = agent?.services.find((s) => s.name === call.service);
  const sendsBody = call.method === 'POST' || call.method === 'PUT' || call.method === 'PATCH' || call.method === 'DELETE';
  const templated = call.path.includes('{{');
  const verdict = service && !templated ? matchServiceRequest([service], service.name, call.method, call.path) : null;
  const queryText = Object.keys(call.query).length ? `?${Object.keys(call.query).map((k) => `${encodeURIComponent(k)}=…`).join('&')}` : '';

  /** Choosing a service keeps the method and path when it allows them, else takes its first method and first path. */
  const pickService = (s: SiteServiceDTO | undefined, name: string) => {
    if (!s) return onChange({ service: name });
    const method = (s.methods as readonly string[]).includes(call.method) ? call.method : (s.methods[0] as GatewayMethod);
    const firstPath = s.healthPath ?? s.paths[0]?.replace(/\/\*$/, '/') ?? '/';
    onChange({ service: s.name, method, path: call.path && call.path !== '/' ? call.path : firstPath });
  };

  return (
    <div className="site-call" data-testid="site-call-fields">
      <div className="field"><label htmlFor={`${idPrefix}-agent`}>Storage agent</label>
        <select id={`${idPrefix}-agent`} className="select" value={call.agentId} data-testid="site-call-agent"
          onChange={(e) => {
            const a = agents.find((x) => x.agentId === e.target.value);
            const first = a?.services[0];
            onChange({ agentId: e.target.value, ...(first ? { service: first.name, method: first.methods[0] as GatewayMethod, path: first.healthPath ?? first.paths[0]?.replace(/\/\*$/, '/') ?? '/' } : {}) });
          }}>
          <option value={NO_AGENT}>Choose…</option>
          {usable.map((a) => <option key={a.agentId} value={a.agentId}>{a.agentName} · {a.site}{a.connected ? '' : ' (not connected)'}</option>)}
          {agent && agent.state !== 'on' && <option value={agent.agentId}>{agent.agentName} (gateway {agent.state === 'too_old' ? 'not available' : 'off'})</option>}
          {!agent && call.agentId !== NO_AGENT && <option value={call.agentId}>Unknown agent</option>}
        </select>
        <span className="help">
          {q.isLoading ? 'Loading the agents’ gateways…'
            : usable.length === 0 ? <>No storage agent has its API gateway on. A site turns it on in the agent’s own configuration; see <Link to="/admin/webhooks">Administration → Webhooks</Link> → Site services.</>
              : <>Only agents whose API gateway is on. The site lists what may be called in the agent’s gateway.json.</>}
        </span>
      </div>
      {agent && (
        <div className="field"><label htmlFor={`${idPrefix}-service`}>Service</label>
          <select id={`${idPrefix}-service`} className="select" value={call.service} data-testid="site-call-service"
            onChange={(e) => pickService(agent.services.find((s) => s.name === e.target.value), e.target.value)}>
            {!service && <option value={call.service}>{call.service === 'service' ? 'Choose…' : `${call.service} (not reported)`}</option>}
            {agent.services.map((s) => <option key={s.name} value={s.name}>{s.name}{s.description ? ` · ${s.description}` : ''}</option>)}
          </select>
          {service && <span className="help">{service.origin}</span>}
        </div>
      )}
      {service && (
        <>
          <div className="field"><label htmlFor={`${idPrefix}-method`}>Method</label>
            <select id={`${idPrefix}-method`} className="select" value={call.method} data-testid="site-call-method"
              onChange={(e) => onChange({ method: e.target.value as GatewayMethod, ...(e.target.value === 'GET' || e.target.value === 'HEAD' ? { payload: {} } : {}) })}>
              {!service.methods.includes(call.method) && <option value={call.method}>{call.method} (not allowed)</option>}
              {service.methods.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div className="field"><label htmlFor={`${idPrefix}-path`}>Path</label>
            <input id={`${idPrefix}-path`} className="input mono" value={call.path} placeholder="/stories/{{asset.metadata.f_story_id}}" data-testid="site-call-path"
              onChange={(e) => onChange({ path: e.target.value })} list={`${idPrefix}-paths`} />
            <datalist id={`${idPrefix}-paths`}>{service.paths.map((p) => <option key={p} value={p.replace(/\/\*$/, '/')} />)}</datalist>
            <span className="help" data-testid="site-call-path-hint">
              Allowed: <span className="mono">{service.paths.join(', ')}</span> (<span className="mono">*</span> is one segment; last, the rest of the path).
              {templated ? ' Templates are filled in when it runs, then checked.' : verdict && !verdict.ok ? <span className="site-call-bad"> {verdict.reason}</span> : null}
            </span>
          </div>
          <div className="field"><span className="label">Address</span>
            <span className="readonly-value mono site-call-preview" data-testid="site-call-preview">{service.origin}…{verdict?.ok ? verdict.path : call.path}{queryText}</span>
            <span className="help">The agent adds the service’s own address and its secret headers; PRISM never sees them.</span>
          </div>
        </>
      )}
      <Rows label="Query parameters" testId="site-call-query" rows={call.query} onChange={(query) => onChange({ query })} />
      {sendsBody && <Rows label="JSON body" testId="site-call-payload" rows={call.payload} onChange={(payload) => onChange({ payload })}
        help={<>Each value is templated text; a value that is only <span className="mono">{'{{json path}}'}</span> is sent as that value (a number, an object…).</>} />}
      <p className="help">{answer}</p>
    </div>
  );
}

function Rows({ label, testId, rows, onChange, help }: { label: string; testId: string; rows: Record<string, string>; onChange: (rows: Record<string, string>) => void; help?: ReactNode }) {
  const [key, setKey] = useState('');
  const add = () => {
    const k = key.trim();
    if (!k || k in rows) return;
    onChange({ ...rows, [k]: '' });
    setKey('');
  };
  return (
    <div className="field" data-testid={testId}><span className="label">{label}</span>
      {Object.entries(rows).map(([k, v]) => (
        <div className="condition-row" key={k}>
          <span className="readonly-value mono" style={{ minWidth: 100 }}>{k}</span>
          <input className="input" value={v} aria-label={`${label} ${k}`} onChange={(e) => onChange({ ...rows, [k]: e.target.value })} />
          <button type="button" className="btn ghost icon sm" aria-label={`Remove ${k}`} onClick={() => { const next = { ...rows }; delete next[k]; onChange(next); }}><Trash2 size={12} /></button>
        </div>
      ))}
      <div className="condition-row">
        <input className="input mono" value={key} placeholder="name" aria-label={`New ${label.toLowerCase()} name`} onChange={(e) => setKey(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} />
        <button type="button" className="btn ghost sm" onClick={add} disabled={!key.trim()}><Plus size={13} /> Add</button>
      </div>
      {help && <span className="help">{help}</span>}
    </div>
  );
}
